import { useRecordContext } from 'ra-core';
import { cn } from '@/lib/utils';

type StatusKey =
    | 'draft' | 'sent' | 'approved' | 'rejected'
    | 'paid' | 'overdue' | 'delivered' | 'converted'
    | 'pending_approval' | 'blacklisted'
    | 'new' | 'in_discussion' | 'qualified' | 'disqualified'
    | 'active' | 'on_hold' | 'completed' | 'cancelled'
    | string;

const STATUS_CONFIG: Record<string, { label: string; classes: string }> = {
    draft:            { label: 'Draft',            classes: 'bg-slate-100 text-slate-700 border-slate-300' },
    sent:             { label: 'Sent',             classes: 'bg-blue-100 text-blue-700 border-blue-300' },
    approved:         { label: 'Approved',         classes: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
    rejected:         { label: 'Rejected',         classes: 'bg-red-100 text-red-700 border-red-300' },
    revised:          { label: 'Revised',          classes: 'bg-amber-100 text-amber-700 border-amber-300' },
    paid:             { label: 'Paid',             classes: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
    partial:          { label: 'Partial',          classes: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
    overdue:          { label: 'Overdue',          classes: 'bg-orange-100 text-orange-700 border-orange-300' },
    delivered:        { label: 'Delivered',        classes: 'bg-teal-100 text-teal-700 border-teal-300' },
    converted:        { label: 'Converted',        classes: 'bg-purple-100 text-purple-700 border-purple-300' },
    pending_approval: { label: 'Pending Approval', classes: 'bg-amber-100 text-amber-700 border-amber-300' },
    checked:          { label: 'Checked',          classes: 'bg-cyan-100 text-cyan-700 border-cyan-300' },
    conditional:      { label: 'Conditional',      classes: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
    blacklisted:      { label: 'Blacklisted',      classes: 'bg-red-200 text-red-900 border-red-400' },
    new:              { label: 'New',              classes: 'bg-sky-100 text-sky-700 border-sky-300' },
    in_discussion:    { label: 'In Discussion',    classes: 'bg-indigo-100 text-indigo-700 border-indigo-300' },
    qualified:        { label: 'Qualified',        classes: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
    disqualified:     { label: 'Disqualified',     classes: 'bg-red-100 text-red-700 border-red-300' },
    closed:           { label: 'Closed',           classes: 'bg-slate-200 text-slate-600 border-slate-400' },
    active:           { label: 'Active',           classes: 'bg-blue-100 text-blue-700 border-blue-300' },
    on_hold:          { label: 'On Hold',          classes: 'bg-amber-100 text-amber-700 border-amber-300' },
    completed:        { label: 'Completed',        classes: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
    cancelled:        { label: 'Cancelled',        classes: 'bg-slate-200 text-slate-600 border-slate-300' },
    in_progress:      { label: 'In Progress',      classes: 'bg-blue-100 text-blue-700 border-blue-300' },
    received:         { label: 'Received',         classes: 'bg-teal-100 text-teal-700 border-teal-300' },
    flagged:          { label: 'Flagged',          classes: 'bg-red-100 text-red-700 border-red-300' },
    qa_passed:        { label: 'QA Passed',        classes: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
    qa_failed:        { label: 'QA Failed',        classes: 'bg-red-100 text-red-700 border-red-300' },
};

interface StatusBadgeProps {
    status: StatusKey;
    className?: string;
}

export const StatusBadge = ({ status, className }: StatusBadgeProps) => {
    const config = STATUS_CONFIG[status] ?? {
        label: status.replace(/_/g, ' '),
        classes: 'bg-slate-100 text-slate-700 border-slate-300',
    };

    return (
        <span
            className={cn(
                'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border capitalize',
                config.classes,
                className
            )}
        >
            {config.label}
        </span>
    );
};

/** Drop-in replacement for TextField showing a status badge when used inside react-admin Show/List */
export const StatusBadgeField = ({ source }: { record?: any; source: string }) => {
    const record = useRecordContext();
    const status = record?.[source] ?? '';
    if (!status) return <span className="text-slate-400 text-xs">—</span>;
    return <StatusBadge status={status} />;
};
